import type { RealityRadarImportSnapshot } from "./types.ts";

export interface RealityRadarSnapshotStore {
  save(snapshot: RealityRadarImportSnapshot): Promise<void>;
  get(id: string): Promise<RealityRadarImportSnapshot | undefined>;
  list(): Promise<RealityRadarImportSnapshot[]>;
}

const DATABASE_NAME = "fispro-market-imports";
const DATABASE_VERSION = 1;
const STORE_NAME = "reality-radar-snapshots";

const openDatabase = () => new Promise<IDBDatabase>((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.onerror = () => reject(request.error ?? new Error("Importné snapshoty sa nepodarilo otvoriť."));
  request.onupgradeneeded = () => {
    const database = request.result;
    if (!database.objectStoreNames.contains(STORE_NAME)) {
      const store = database.createObjectStore(STORE_NAME, { keyPath: "id" });
      store.createIndex("capturedAt", "capturedAt");
    }
  };
  request.onsuccess = () => resolve(request.result);
});

const requestResult = <Value>(request: IDBRequest<Value>) => new Promise<Value>((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error("Importný snapshot sa nepodarilo načítať."));
});

export class IndexedDbRealityRadarSnapshotStore implements RealityRadarSnapshotStore {
  async save(snapshot: RealityRadarImportSnapshot) {
    const database = await openDatabase();
    try {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      await requestResult(transaction.objectStore(STORE_NAME).add(snapshot));
    } finally {
      database.close();
    }
  }

  async get(id: string) {
    const database = await openDatabase();
    try {
      return await requestResult<RealityRadarImportSnapshot | undefined>(
        database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(id)
      );
    } finally {
      database.close();
    }
  }

  async list() {
    const database = await openDatabase();
    try {
      const snapshots = await requestResult<RealityRadarImportSnapshot[]>(
        database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).getAll()
      );
      return snapshots.sort((first, second) => second.capturedAt.localeCompare(first.capturedAt));
    } finally {
      database.close();
    }
  }
}

export class MemoryRealityRadarSnapshotStore implements RealityRadarSnapshotStore {
  private readonly snapshots = new Map<string, RealityRadarImportSnapshot>();

  async save(snapshot: RealityRadarImportSnapshot) {
    if (this.snapshots.has(snapshot.id)) throw new Error("Importný snapshot už existuje.");
    this.snapshots.set(snapshot.id, structuredClone(snapshot));
  }

  async get(id: string) {
    const snapshot = this.snapshots.get(id);
    return snapshot ? structuredClone(snapshot) : undefined;
  }

  async list() {
    return [...this.snapshots.values()]
      .sort((first, second) => second.capturedAt.localeCompare(first.capturedAt))
      .map((snapshot) => structuredClone(snapshot));
  }
}
